import { useClientPortalAPI } from "@certn/api";
import { components } from "@certn/api/generated-schemas/client-portal";
import { Text, Stack, Link } from "@certn/cascadia";
import {
  ColumnDefinition,
  Container,
  DataTable,
  MediaQuery,
  NeutralTag,
  Pagination,
  SuccessTag,
} from "@certn/cascadia-react";
import { Trans, useTranslation } from "react-i18next";

import { ActionsDropdown } from "./components/ActionsDropdown";
import { AddTagModal } from "./components/AddTagModal";
import { TagFilter } from "./components/TagFilter";
import { FilterBar } from "../../components/filters/FilterBar";
import { PageTitle } from "../../components/page-title/PageTitle";
import { useQueryParams } from "../../hooks/useQueryParams";
import { queryParamsToApiParams } from "../utils";

export function TagsListView() {
  const { t } = useTranslation();
  const { params, setParam } = useQueryParams();
  const { data } = useClientPortalAPI(
    `/api/internal/client-portal/tags/`,
    queryParamsToApiParams(params)
  );

  const columns: ColumnDefinition<
    components["schemas"]["TagList"][][number]
  >[] = [
    {
      key: "name",
      name: t("Name"),
      width: 480,
      isRowHeader: true,
      testID: "name-column-header",
      render({ name }) {
        return (
          <NeutralTag type="subtle" testID="tag-name">
            {name}
          </NeutralTag>
        );
      },
    },
    {
      key: "is_active",
      name: t("Status"),
      width: 172,
      isRowHeader: true,
      testID: "status-column-header",
      render({ is_active }) {
        return is_active ? (
          <SuccessTag testID="active-tag" type="subtle">
            {t("Active")}
          </SuccessTag>
        ) : (
          <NeutralTag type="subtle" testID="deactivated-tag">
            {t("Deactivated")}
          </NeutralTag>
        );
      },
    },
    {
      key: "actions",
      isAction: true,
      width: 32,
      render({ name, is_active, id }) {
        return <ActionsDropdown tagName={name} isActive={is_active} id={id} />;
      },
    },
  ];
  return (
    <Container layout="full-width">
      <PageTitle actions={<AddTagModal />}>{t("Tags")}</PageTitle>
      <MediaQuery sm type="hide">
        <Stack orientation="horizontal" gap="space-2">
          <Text color="text-weak">
            <Trans>
              View and manage tags that can be added to cases. Tags help you
              filter, organize and keep track of your cases.
            </Trans>
          </Text>
          <Link
            target="_blank"
            href="https://portal.certn.co/hc/en-us/articles/31158218031251-Case-tags"
            textStyle="global/body-secondary"
            linkStyle="monochrome"
            testID="tags-learn-more-link"
          >
            <Trans>Learn more</Trans>
          </Link>
        </Stack>
      </MediaQuery>
      <FilterBar hasSearch testID="tags">
        <TagFilter />
      </FilterBar>
      <DataTable
        columns={columns}
        rows={data?.results || []}
        aria-label="tags table"
        testId="tags-table"
      />
      <Pagination
        setPage={(page) => setParam("page", page.toString())}
        setPageCount={(pageCount) =>
          setParam("page_size", pageCount.toString())
        }
        total={data?.count ?? 0}
        page={params.page ? Number(params.page) : 1}
        pageCount={params["page_size"] ? Number(params["page_size"]) : 25}
      />
    </Container>
  );
}
